import { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonInput,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_TEAM_NAME } from '../../../../graphql/mutations/fantasyLeagueMember';
import { GET_TEAM_TAB_DATA } from '../../../../graphql/queries/teamTabData';
import type {
  UpdateTeamNameMutation,
  UpdateTeamNameMutationVariables,
} from '../../../../types/graphql-generated';

interface UpdateTeamNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  currentName: string | null | undefined;
  leagueId: string;
}

export const UpdateTeamNameModal = ({
  isOpen,
  onClose,
  memberId,
  currentName,
  leagueId,
}: UpdateTeamNameModalProps) => {
  const [teamName, setTeamName] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setTeamName(currentName || '');
    }
  }

  const [updateTeamName, { loading }] = useMutation<
    UpdateTeamNameMutation,
    UpdateTeamNameMutationVariables
  >(UPDATE_TEAM_NAME, {
    refetchQueries: [
      {
        query: GET_TEAM_TAB_DATA,
        variables: { fantasyLeagueId: leagueId },
      },
    ],
  });

  const handleSubmit = async () => {
    setErrors([]);

    // Frontend validation
    if (!teamName || teamName.trim() === '') {
      setErrors(['El nombre del equipo no puede estar vacío']);
      return;
    }

    if (teamName.length > 32) {
      setErrors(['El nombre del equipo no puede tener más de 32 caracteres']);
      return;
    }

    try {
      const result = await updateTeamName({
        variables: {
          fantasyLeagueMemberId: memberId,
          teamName: teamName.trim(),
        },
      });

      if (result.data?.updateTeamName?.errors && result.data.updateTeamName.errors.length > 0) {
        setErrors(result.data.updateTeamName.errors);
      } else {
        handleClose();
      }
    } catch (error) {
      setErrors(['Error al actualizar el nombre del equipo']);
      console.error('Error updating team name:', error);
    }
  };

  const handleClose = () => {
    setErrors([]);
    setTeamName(currentName || '');
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Cambiar nombre del equipo</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="mb-4">
          <IonInput
            className="text-sm font-semibold"
            type="text"
            label="Nombre del equipo"
            labelPlacement="floating"
            fill="outline"
            value={teamName}
            onIonInput={(e) => setTeamName(e.detail.value || '')}
            placeholder="Introduce el nombre del equipo"
            maxlength={32}
          />
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errors.map((error, index) => (
              <IonText color="danger" key={index}>
                <p className="text-sm">{error}</p>
              </IonText>
            ))}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={loading || !teamName || teamName.trim() === ''}
          >
            {loading ? <IonSpinner name="crescent" /> : 'Cambiar'}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
